import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { usePortfolio } from "@/context/PortfolioContext";
import { fmtINR, fmtPct, fmtQty, colorByValue } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Save, Zap, Link2, Unlink } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";

const keyOf = (h) => `${h.symbol}:${h.exchange || "NSE"}`;

export default function Prices() {
  const { activeId } = usePortfolio();
  const [holdings, setHoldings] = useState([]);
  const [summary, setSummary] = useState(null);
  const [prices, setPrices] = useState({});
  const [pricesMeta, setPricesMeta] = useState({});
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [savingAll, setSavingAll] = useState(false);
  const [fetchingLive, setFetchingLive] = useState(false);
  const [liveErrors, setLiveErrors] = useState({}); // {SYMBOL:EXCHANGE: error_msg}
  const [providers, setProviders] = useState({
    upstox_configured: false,
    upstox_connected_oauth: false,
    upstox_expired: false,
    upstox_can_connect: false,
    upstox_expires_at: null,
    default_provider: "yahoo",
  });
  const [connecting, setConnecting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const loadProviders = () => {
    api.get("/system/providers").then((r) => setProviders(r.data)).catch(() => {});
  };

  useEffect(() => {
    loadProviders();
  }, []);

  // Handle OAuth callback redirect (?upstox=connected | error)
  useEffect(() => {
    const status = searchParams.get("upstox");
    if (!status) return;
    if (status === "connected") {
      toast.success("Upstox connected — real-time NSE/BSE prices are now active.");
      loadProviders();
    } else if (status === "error") {
      const msg = searchParams.get("message") || "unknown_error";
      toast.error(`Upstox connect failed: ${msg.replace(/_/g, " ")}`);
    }
    // Strip the params from the URL so the toast doesn't fire again on refresh
    const next = new URLSearchParams(searchParams);
    next.delete("upstox");
    next.delete("message");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);

  const connectUpstox = async () => {
    setConnecting(true);
    try {
      const r = await api.get("/upstox/connect");
      // Full-page redirect to Upstox login — preserves session cookie
      window.location.href = r.data.auth_url;
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not start Upstox connect flow";
      toast.error(detail);
      setConnecting(false);
    }
  };

  const disconnectUpstox = async () => {
    if (!window.confirm("Disconnect Upstox? You'll need to log in again to refresh tokens (~daily).")) return;
    try {
      await api.post("/upstox/disconnect");
      toast.success("Upstox disconnected — falling back to Yahoo Finance.");
      loadProviders();
    } catch {
      toast.error("Could not disconnect Upstox");
    }
  };

  const formatExpiry = (iso) => {
    if (!iso) return "";
    try {
      const ms = new Date(iso).getTime() - new Date().getTime();
      if (ms <= 0) return "expired";
      const hrs = Math.floor(ms / 3600000);
      const mins = Math.floor((ms % 3600000) / 60000);
      return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
    } catch {
      return "";
    }
  };

  const load = async () => {
    try {
      const r = await api.get("/portfolio/summary");
      setHoldings(r.data.holdings || []);
      setPrices(r.data.prices || {});
      setPricesMeta(r.data.prices_meta || {});
      setSummary(r.data.summary || null);
      const d = {};
      (r.data.holdings || []).forEach((h) => {
        const k = keyOf(h);
        d[k] = (r.data.prices?.[k] ?? h.current_price ?? "").toString();
      });
      setDraft(d);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (!activeId) return;
    let cancelled = false;
    (async () => {
      await Promise.resolve();
      if (cancelled) return;
      setLoading(true);
      await load();
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const fetchLive = async () => {
    setFetchingLive(true);
    try {
      const r = await api.post("/prices/refresh", {});
      const okN = r.data.updated?.length || 0;
      const errN = r.data.errors?.length || 0;
      const errMap = {};
      (r.data.errors || []).forEach((e) => { errMap[`${e.symbol}:${e.exchange}`] = e.error; });
      setLiveErrors(errMap);
      if (okN > 0 && errN === 0) toast.success(`Live prices fetched for ${okN} holding${okN > 1 ? "s" : ""}`);
      else if (okN > 0 && errN > 0) {
        const failed = (r.data.errors || []).slice(0, 4).map(e => e.symbol).join(", ");
        const more = errN > 4 ? ` +${errN - 4} more` : "";
        toast.warning(`${okN} updated · ${errN} failed: ${failed}${more}`, { duration: 7000 });
      }
      else if (errN > 0) {
        const sample = (r.data.errors || [])[0];
        toast.error(`No prices fetched. ${sample?.symbol}: ${sample?.error?.slice(0, 100)}`, { duration: 7000 });
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Live refresh failed");
    } finally {
      setFetchingLive(false);
    }
  };

  const activeHoldings = useMemo(() => holdings.filter((h) => h.quantity > 0), [holdings]);

  // Live, client-side preview of metrics based on current draft prices.
  const preview = useMemo(() => {
    const rows = activeHoldings.map((h) => {
      const k = keyOf(h);
      const draftPrice = parseFloat(draft[k]);
      const usingDraft = !isNaN(draftPrice) && draftPrice >= 0;
      const price = usingDraft ? draftPrice : h.current_price;
      const currentValue = h.quantity * price;
      const unrealized = currentValue - h.invested;
      const unrealizedPct = h.invested > 0 ? (unrealized / h.invested) * 100 : 0;
      const changed = usingDraft && Math.abs(draftPrice - (prices[k] ?? h.current_price ?? 0)) > 1e-9;
      return {
        ...h,
        key: k,
        previewPrice: price,
        previewCurrentValue: currentValue,
        previewUnrealized: unrealized,
        previewUnrealizedPct: unrealizedPct,
        changed,
        meta: pricesMeta[k] || {},
      };
    });
    const total_current_value = rows.reduce((s, r) => s + r.previewCurrentValue, 0);
    const total_unrealized = rows.reduce((s, r) => s + r.previewUnrealized, 0);
    const realized = Number(summary?.realized_gain || 0);
    const dividends = Number(summary?.dividends || 0);
    const capital_deployed = Number(summary?.total_capital_deployed || 0);
    const absolute_gain = total_unrealized + realized + dividends;
    const absolute_return_pct = capital_deployed > 0 ? (absolute_gain / capital_deployed) * 100 : 0;
    return { rows, total_current_value, total_unrealized, realized, dividends, capital_deployed, absolute_gain, absolute_return_pct };
  }, [activeHoldings, draft, prices, pricesMeta, summary]);

  const save = async (h) => {
    const k = keyOf(h);
    const val = parseFloat(draft[k]);
    if (isNaN(val) || val < 0) {
      toast.error("Enter a valid price");
      return;
    }
    setSaving((s) => ({ ...s, [k]: true }));
    try {
      await api.put("/prices", { symbol: h.symbol, exchange: h.exchange, price: val });
      toast.success(`${h.symbol} (${h.exchange}) updated`);
      await load();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving((s) => ({ ...s, [k]: false }));
    }
  };

  const saveAll = async () => {
    const changed = preview.rows.filter((r) => r.changed);
    if (changed.length === 0) {
      toast.info("No price changes to save");
      return;
    }
    setSavingAll(true);
    try {
      await Promise.all(
        changed.map((r) => api.put("/prices", { symbol: r.symbol, exchange: r.exchange, price: parseFloat(draft[r.key]) }))
      );
      toast.success(`${changed.length} price${changed.length > 1 ? "s" : ""} saved — portfolio recalculated`);
      await load();
    } catch {
      toast.error("Some prices failed to save");
    } finally {
      setSavingAll(false);
    }
  };

  const anyChanged = preview.rows.some((r) => r.changed);

  return (
    <Layout
      subtitle="Manual prices"
      title="Update current market prices"
      actions={
        activeHoldings.length > 0 && (
          <>
            <Button
              data-testid="fetch-live-prices"
              onClick={fetchLive}
              disabled={fetchingLive}
              variant="outline"
              className="rounded px-3 sm:px-5 py-2.5 text-xs sm:text-sm"
            >
              <Zap className={`w-4 h-4 sm:mr-2 ${fetchingLive ? "animate-pulse" : ""}`} strokeWidth={1.75} />
              <span className="hidden sm:inline">{fetchingLive ? "Fetching…" : "Fetch live"}</span>
              <span className="sm:hidden ml-1.5">{fetchingLive ? "…" : "Live"}</span>
            </Button>
            <Button
              data-testid="save-all-prices"
              onClick={saveAll}
              disabled={savingAll || !anyChanged}
              className="rounded px-3 sm:px-5 py-2.5 text-xs sm:text-sm"
              style={anyChanged ? { background: "var(--btn-primary-bg)", color: "var(--btn-primary-fg)" } : { background: "var(--tint-neutral-bg)", color: "var(--text-muted)" }}
            >
              <Save className="w-4 h-4 sm:mr-2" strokeWidth={1.75} />
              <span className="hidden sm:inline">{savingAll ? "Saving…" : "Save all changes"}</span>
              <span className="sm:hidden ml-1.5">{savingAll ? "…" : "Save all"}</span>
            </Button>
          </>
        )
      }
    >
      <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="provider-chip">
        <span className="label-overline text-[10px]">Data source</span>
        <span
          className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded"
          style={{
            background: providers.upstox_connected_oauth
              ? "var(--tint-accent-bg)"
              : providers.upstox_configured
              ? "var(--tint-accent-bg)"
              : "var(--tint-neutral-bg)",
            color: providers.upstox_connected_oauth || providers.upstox_configured ? "var(--tint-accent-fg)" : "var(--tint-neutral-fg)",
          }}
          title={providers.upstox_connected_oauth
            ? `Upstox OAuth — token expires in ${formatExpiry(providers.upstox_expires_at)}`
            : providers.upstox_configured
            ? "Upstox Analytics — real-time NSE/BSE quotes"
            : "Yahoo Finance — ~15 min delayed"}
        >
          {providers.upstox_connected_oauth
            ? `Upstox · Real-time · ${formatExpiry(providers.upstox_expires_at)}`
            : providers.upstox_configured
            ? "Upstox · Real-time"
            : "Yahoo · ~15 min delay"}
        </span>
        {providers.upstox_expired && (
          <span
            className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded"
            style={{ background: "var(--tint-loss-bg)", color: "var(--accent-negative)" }}
            data-testid="upstox-expired-chip"
          >
            Token expired — reconnect
          </span>
        )}
        {providers.upstox_can_connect && !providers.upstox_connected_oauth && (
          <Button
            data-testid="connect-upstox-button"
            onClick={connectUpstox}
            disabled={connecting}
            variant="outline"
            className="rounded px-3 py-0.5 h-7 text-[11px] font-medium"
          >
            <Link2 className="w-3 h-3 mr-1.5" strokeWidth={1.75} />
            {connecting ? "Redirecting…" : "Connect Upstox"}
          </Button>
        )}
        {providers.upstox_connected_oauth && (
          <Button
            data-testid="disconnect-upstox-button"
            onClick={disconnectUpstox}
            variant="outline"
            className="rounded px-3 py-0.5 h-7 text-[11px] font-medium"
            style={{ color: "var(--text-muted)" }}
          >
            <Unlink className="w-3 h-3 mr-1.5" strokeWidth={1.75} />
            Disconnect
          </Button>
        )}
      </div>
      <p className="text-sm mb-6 max-w-2xl" style={{ color: "var(--text-secondary)" }}>
        Tap <strong>Fetch live</strong> to pull current NSE/BSE prices from
        {providers.upstox_configured ? " Upstox (real-time, tick-accurate)" : " Yahoo Finance (~15 min delay)"},
        or type a manual price below. As you edit, the table previews the new current value, unrealized
        gain, and the impact on your overall absolute return.
      </p>

      {loading ? (
        <div className="surface-card p-10 text-center label-overline">Loading…</div>
      ) : activeHoldings.length === 0 ? (
        <div className="surface-card p-12 text-center" data-testid="prices-empty">
          <p className="font-display text-xl mb-2">No active holdings</p>
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>
            Add BUY transactions first to populate this list.
          </p>
        </div>
      ) : (
        <>
          {/* Live preview totals */}
          <section
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6"
            data-testid="prices-preview-totals"
          >
            <PreviewStat label="Current Value" value={fmtINR(preview.total_current_value)} />
            <PreviewStat
              label="Unrealized Gain"
              value={fmtINR(preview.total_unrealized, { showSign: true })}
              tone={preview.total_unrealized}
            />
            <PreviewStat
              label="Absolute Gain"
              value={fmtINR(preview.absolute_gain, { showSign: true })}
              tone={preview.absolute_gain}
              hint="Unrealized + Realized + Dividends"
            />
            <PreviewStat
              label="Absolute Return"
              value={fmtPct(preview.absolute_return_pct)}
              tone={preview.absolute_return_pct}
              hint="vs. capital deployed"
            />
          </section>

          <section className="surface-card overflow-hidden" data-testid="prices-table">
            {/* Mobile cards (< md) */}
            <div className="md:hidden flex flex-col divide-y" style={{ borderColor: "var(--border-soft)" }}>
              {preview.rows.map((h) => (
                <PriceCard
                  key={`${h.key}-card`}
                  h={h}
                  draftValue={draft[h.key] ?? ""}
                  onChange={(v) => setDraft({ ...draft, [h.key]: v })}
                  onSave={() => save(h)}
                  saving={!!saving[h.key]}
                  liveError={liveErrors[h.key]}
                />
              ))}
            </div>

            {/* Desktop table (>= md) */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead style={{ background: "var(--bg-base)" }}>
                  <tr>
                    <Th>Symbol</Th>
                    <Th right>Quantity</Th>
                    <Th right>Avg cost</Th>
                    <Th right>Current price (₹)</Th>
                    <Th right>Current value</Th>
                    <Th right>Unrealized</Th>
                    <Th right>Realized</Th>
                    <Th right>Dividends</Th>
                    <Th right>Action</Th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((h) => (
                    <tr
                      key={h.key}
                      className="border-t"
                      style={{
                        borderColor: "var(--border-soft)",
                        background: h.changed ? "rgba(111,211,196,0.06)" : "transparent",
                      }}
                      data-testid={`price-row-${h.symbol}`}
                    >
                      <td className="py-4 px-6">
                        <div className="font-medium flex items-center gap-2">
                          {h.symbol}
                          <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: h.exchange === "BSE" ? "var(--tint-neutral-bg)" : "var(--tint-accent-bg)", color: h.exchange === "BSE" ? "var(--tint-neutral-fg)" : "var(--tint-accent-fg)" }}>
                            {h.exchange || "NSE"}
                          </span>
                          {h.changed && (
                            <span
                              className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded"
                              style={{ background: "var(--tint-caution-bg)", color: "var(--tint-caution-fg)" }}
                              data-testid={`unsaved-${h.symbol}`}
                            >
                              unsaved
                            </span>
                          )}
                          {(h.meta?.source === "yahoo" || h.meta?.source === "upstox") && !h.changed && (
                            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: h.meta?.source === "upstox" ? "var(--tint-accent-bg)" : "var(--tint-neutral-bg)", color: h.meta?.source === "upstox" ? "var(--tint-accent-fg)" : "var(--tint-neutral-fg)" }} title={`${h.meta?.updated_at || ""}${h.meta?.resolved_symbol ? ` · matched as ${h.meta.resolved_symbol}` : ""}`}>
                              {h.meta?.source === "upstox" ? "live · upstox" : "live · yahoo"}
                            </span>
                          )}
                          {liveErrors[h.key] && (
                            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "var(--tint-loss-bg)", color: "var(--accent-negative)" }} title={liveErrors[h.key]}>
                              no quote
                            </span>
                          )}
                        </div>
                        {h.company_name && (
                          <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                            {h.company_name}
                          </div>
                        )}
                        {h.meta?.resolved_symbol && h.meta.resolved_symbol.replace(/\.(NS|BO)$/i, "") !== h.symbol && (
                          <div className="text-[11px] mt-0.5" style={{ color: "var(--text-muted)" }} data-testid={`resolved-${h.symbol}`}>
                            matched <span className="font-mono">{h.meta.resolved_symbol}</span>
                            {h.meta.resolved_name ? ` · ${h.meta.resolved_name}` : ""}
                          </div>
                        )}
                        {liveErrors[h.key] && (
                          <div className="text-[11px] mt-0.5" style={{ color: "var(--accent-negative)" }}>
                            {liveErrors[h.key]}
                          </div>
                        )}
                      </td>
                      <td className="py-4 px-6 text-right font-mono">{fmtQty(h.quantity)}</td>
                      <td className="py-4 px-6 text-right font-mono">{fmtINR(h.avg_cost)}</td>
                      <td className="py-4 px-6 text-right">
                        <Input
                          data-testid={`price-input-${h.symbol}`}
                          type="number"
                          step="any"
                          value={draft[h.key] ?? ""}
                          onChange={(e) => setDraft({ ...draft, [h.key]: e.target.value })}
                          className="rounded-lg w-32 ml-auto text-right font-mono"
                          placeholder="0.00"
                        />
                      </td>
                      <td
                        className="py-4 px-6 text-right font-mono"
                        data-testid={`current-value-${h.symbol}`}
                      >
                        {fmtINR(h.previewCurrentValue)}
                      </td>
                      <td
                        className={`py-4 px-6 text-right font-mono ${colorByValue(h.previewUnrealized)}`}
                        data-testid={`unrealized-${h.symbol}`}
                      >
                        <div>{fmtINR(h.previewUnrealized, { showSign: true })}</div>
                        <div
                          className="text-[10px] font-normal"
                          style={{ color: "var(--text-muted)" }}
                        >
                          {fmtPct(h.previewUnrealizedPct)}
                        </div>
                      </td>
                      <td
                        className={`py-4 px-6 text-right font-mono ${colorByValue(h.realized_gain)}`}
                        data-testid={`realized-${h.symbol}`}
                      >
                        {fmtINR(h.realized_gain, { showSign: true })}
                      </td>
                      <td className="py-4 px-6 text-right font-mono" data-testid={`dividends-${h.symbol}`}>
                        {fmtINR(h.dividends)}
                      </td>
                      <td className="py-4 px-6 text-right">
                        <Button
                          data-testid={`save-price-${h.symbol}`}
                          size="sm"
                          onClick={() => save(h)}
                          disabled={saving[h.key] || !h.changed}
                          className="rounded-lg"
                          style={{ background: h.changed ? "var(--btn-primary-bg)" : "var(--tint-neutral-bg)", color: h.changed ? "var(--btn-primary-fg)" : "var(--text-muted)" }}
                        >
                          {saving[h.key] ? "Saving…" : "Save"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: "1px solid var(--border-soft)", background: "var(--bg-base)" }}>
                    <td className="py-4 px-6 label-overline">Totals</td>
                    <td className="py-4 px-6"></td>
                    <td className="py-4 px-6"></td>
                    <td className="py-4 px-6"></td>
                    <td className="py-4 px-6 text-right font-mono font-semibold" data-testid="total-current-value">
                      {fmtINR(preview.total_current_value)}
                    </td>
                    <td
                      className={`py-4 px-6 text-right font-mono font-semibold ${colorByValue(preview.total_unrealized)}`}
                      data-testid="total-unrealized"
                    >
                      {fmtINR(preview.total_unrealized, { showSign: true })}
                    </td>
                    <td
                      className={`py-4 px-6 text-right font-mono font-semibold ${colorByValue(preview.realized)}`}
                      data-testid="total-realized"
                    >
                      {fmtINR(preview.realized, { showSign: true })}
                    </td>
                    <td className="py-4 px-6 text-right font-mono font-semibold" data-testid="total-dividends">
                      {fmtINR(preview.dividends)}
                    </td>
                    <td className="py-4 px-6"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>
        </>
      )}
    </Layout>
  );
}

function PreviewStat({ label, value, tone, hint }) {
  const toneClass = tone === undefined ? "" : colorByValue(tone);
  return (
    <div className="surface-card p-4 md:p-5">
      <p className="label-overline text-[10px] md:text-[0.7rem]">{label}</p>
      <p className={`font-metric text-lg md:text-2xl font-light mt-1.5 ${toneClass}`}>{value}</p>
      {hint && (
        <p className="text-[10px] md:text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>
          {hint}
        </p>
      )}
    </div>
  );
}

function PriceCard({ h, draftValue, onChange, onSave, saving, liveError }) {
  const exchBadge = {
    background: h.exchange === "BSE" ? "var(--tint-neutral-bg)" : "var(--tint-accent-bg)",
    color: h.exchange === "BSE" ? "var(--tint-neutral-fg)" : "var(--tint-accent-fg)",
  };
  return (
    <div
      className="px-4 py-4"
      style={{ background: h.changed ? "rgba(111,211,196,0.06)" : "transparent" }}
      data-testid={`price-card-${h.symbol}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-medium text-sm">{h.symbol}</span>
            <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded" style={exchBadge}>
              {h.exchange || "NSE"}
            </span>
            {h.changed && (
              <span
                className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded"
                style={{ background: "var(--tint-caution-bg)", color: "var(--tint-caution-fg)" }}
                data-testid={`unsaved-${h.symbol}-mobile`}
              >
                unsaved
              </span>
            )}
            {(h.meta?.source === "yahoo" || h.meta?.source === "upstox") && !h.changed && (
              <span
                className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                style={{
                  background: h.meta?.source === "upstox" ? "var(--tint-accent-bg)" : "var(--tint-neutral-bg)",
                  color: h.meta?.source === "upstox" ? "var(--tint-accent-fg)" : "var(--tint-neutral-fg)",
                }}
              >
                {h.meta?.source === "upstox" ? "live · upstox" : "live · yahoo"}
              </span>
            )}
            {liveError && (
              <span
                className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                style={{ background: "var(--tint-loss-bg)", color: "var(--accent-negative)" }}
              >
                no quote
              </span>
            )}
          </div>
          {h.company_name && (
            <div className="text-[11px] mt-0.5 truncate" style={{ color: "var(--text-muted)" }}>
              {h.company_name}
            </div>
          )}
          <div className="text-[11px] mt-0.5 font-mono" style={{ color: "var(--text-muted)" }}>
            {fmtQty(h.quantity)} @ avg {fmtINR(h.avg_cost)}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-mono text-sm font-medium" data-testid={`current-value-${h.symbol}-mobile`}>
            {fmtINR(h.previewCurrentValue)}
          </div>
          <div className={`font-mono text-[11px] ${colorByValue(h.previewUnrealized)}`} data-testid={`unrealized-${h.symbol}-mobile`}>
            {fmtINR(h.previewUnrealized, { showSign: true })} · {fmtPct(h.previewUnrealizedPct)}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <Label className="label-overline text-[9px] mb-1 block">Current price (₹)</Label>
          <Input
            data-testid={`price-input-${h.symbol}-mobile`}
            type="number"
            step="any"
            inputMode="decimal"
            value={draftValue}
            onChange={(e) => onChange(e.target.value)}
            className="rounded-lg w-full text-right font-mono"
            placeholder="0.00"
          />
        </div>
        <Button
          data-testid={`save-price-${h.symbol}-mobile`}
          onClick={onSave}
          disabled={saving || !h.changed}
          className="rounded-lg self-end h-10 px-4"
          style={{ background: h.changed ? "var(--btn-primary-bg)" : "var(--tint-neutral-bg)", color: h.changed ? "var(--btn-primary-fg)" : "var(--text-muted)" }}
        >
          {saving ? "…" : "Save"}
        </Button>
      </div>
    </div>
  );
}

function Th({ children, right }) {
  return (
    <th
      className={`label-overline py-3 px-6 ${right ? "text-right" : "text-left"}`}
      style={{ color: "var(--text-muted)", fontSize: "0.65rem" }}
    >
      {children}
    </th>
  );
}
